import { all, takeEvery, fork, call, put } from "redux-saga/effects";
import { crawlingSettingService } from "@iso/services";
import actions from "./actions";

const CONFLICT_ERROR_CODE = "CRAWLING_EXECUTION_CONFLICT";

export function* searchCrawlingSettings() {
  yield takeEvery(actions.SEARCH_REQUEST, function* ({ payload }) {
    const { keyword, executionType, isActive, latestStatus, page, size, resolve, reject } =
      payload;
    try {
      const response = yield call(crawlingSettingService.search, {
        keyword,
        executionType,
        isActive,
        latestStatus,
        page,
        size,
      });
      resolve && resolve(response);
      const rawItems =
        response?.items ??
        response?.crawlingSettings ??
        (Array.isArray(response) ? response : null);
      const items = Array.isArray(rawItems) ? rawItems : [];
      const total = Number(response?.total);
      yield put({
        type: actions.SEARCH_SUCCESS,
        items,
        total: Number.isFinite(total) ? total : 0,
      });
    } catch (e) {
      yield put({ type: actions.SEARCH_ERROR, error: e });
      reject && reject(e);
    }
  });
}

function* createCrawlingSetting() {
  yield takeEvery(actions.CREATE_REQUEST, function* ({ payload }) {
    const { data, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.create, data);
      yield put({ type: actions.CREATE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.CREATE_ERROR, error: error.response });
      reject(error);
    }
  });
}

function* updateCrawlingSetting() {
  yield takeEvery(actions.UPDATE_REQUEST, function* ({ payload }) {
    const { data, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.update, data);
      yield put({ type: actions.UPDATE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.UPDATE_ERROR, error: error.response });
      reject(error);
    }
  });
}

export function* getCrawlingSettingDetail() {
  yield takeEvery(actions.DETAIL_REQUEST, function* ({ payload }) {
    const { crawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.getDetail, {
        crawlingSettingId,
      });
      resolve && resolve(response);
      yield put({
        type: actions.DETAIL_SUCCESS,
        detail: response,
      });
    } catch (e) {
      yield put({ type: actions.DETAIL_ERROR });
      reject && reject(e);
    }
  });
}

function* deleteCrawlingSetting() {
  yield takeEvery(actions.DELETE_REQUEST, function* ({ payload }) {
    const { crawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.delete, {
        crawlingSettingId,
      });
      yield put({ type: actions.DELETE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.DELETE_ERROR });
      reject(error);
    }
  });
}

function* executeCrawlingSetting() {
  yield takeEvery(actions.EXECUTE_REQUEST, function* ({ payload }) {
    const { crawlingSettingId, forceQueue, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.execute, {
        crawlingSettingId,
        forceQueue,
      });
      yield put({ type: actions.EXECUTE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.EXECUTE_ERROR });
      const conflict = error?.response?.data;
      if (conflict?.code === CONFLICT_ERROR_CODE) {
        reject({
          conflict: true,
          conflictingId: conflict.conflictingId,
          conflictingTitle: conflict.conflictingTitle,
        });
      } else {
        reject(error);
      }
    }
  });
}

function* cancelCrawlingSetting() {
  yield takeEvery(actions.CANCEL_REQUEST, function* ({ payload }) {
    const { crawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.cancel, {
        crawlingSettingId,
      });
      yield put({ type: actions.CANCEL_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.CANCEL_ERROR });
      reject(error);
    }
  });
}

function* getCurrentJob() {
  yield takeEvery(actions.GET_CURRENT_REQUEST, function* ({ payload }) {
    const { crawlingSettingId } = payload;
    try {
      const response = yield call(crawlingSettingService.getCurrent, {
        crawlingSettingId,
      });
      yield put({
        type: actions.GET_CURRENT_SUCCESS,
        currentJob: response,
      });
    } catch (error) {
      yield put({ type: actions.GET_CURRENT_ERROR });
    }
  });
}

function* getHistories() {
  yield takeEvery(actions.GET_HISTORIES_REQUEST, function* ({ payload }) {
    const { crawlingSettingId } = payload;
    try {
      const response = yield call(crawlingSettingService.getHistories, {
        crawlingSettingId,
      });
      const raw = response?.histories ?? response ?? [];
      yield put({
        type: actions.GET_HISTORIES_SUCCESS,
        histories: Array.isArray(raw) ? raw : [],
      });
    } catch (error) {
      yield put({ type: actions.GET_HISTORIES_ERROR });
    }
  });
}

function* validateSettingFile() {
  yield takeEvery(actions.VALIDATE_SETTING_FILE_REQUEST, function* ({ payload }) {
    const { file, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.validateSettingFile, file);
      yield put({ type: actions.VALIDATE_SETTING_FILE_SUCCESS });
      resolve && resolve(response);
    } catch (error) {
      yield put({ type: actions.VALIDATE_SETTING_FILE_ERROR, error: error.response });
      reject && reject(error);
    }
  });
}

function* importExcel() {
  yield takeEvery(actions.IMPORT_REQUEST, function* ({ payload }) {
    const { crawlingSettingId, file, resolve, reject } = payload;
    try {
      const response = yield call(crawlingSettingService.importExcel, {
        crawlingSettingId,
        file,
      });
      yield put({ type: actions.IMPORT_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.IMPORT_ERROR });
      reject(error);
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(searchCrawlingSettings),
    fork(createCrawlingSetting),
    fork(updateCrawlingSetting),
    fork(getCrawlingSettingDetail),
    fork(deleteCrawlingSetting),
    fork(executeCrawlingSetting),
    fork(cancelCrawlingSetting),
    fork(getCurrentJob),
    fork(getHistories),
    fork(importExcel),
    fork(validateSettingFile),
  ]);
}
